// Synthesized alert chime. No audio asset is shipped: no binary in the repo,
// no fetch that can fail, and the whole thing is stubbable in tests.

let cachedContext: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctor =
      (window as any).AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!cachedContext) cachedContext = new Ctor();
    return cachedContext;
  } catch {
    return null;
  }
}

/**
 * Primes the AudioContext from inside a user gesture. Browsers refuse to play
 * audio until the user has interacted with the page, so this must be called
 * from a click handler — see OrderAlertsToggle.
 */
export function unlockAudio(): void {
  try {
    const ctx = getContext();
    if (ctx && ctx.state === 'suspended') {
      const result = ctx.resume();
      // resume() returns a Promise in real browsers but may return undefined in test stubs.
      // Guard for both cases and always catch rejections to prevent unhandled promise rejections.
      if (result?.catch) result.catch(() => {});
    }
  } catch {
    /* audio is a nicety; never let it break the page */
  }
}

/** Two-note rising blip. Silent no-op where Web Audio is unavailable. */
export function playAlertChime(): void {
  try {
    const ctx = getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const NOTES = [880, 1174.66]; // A5 then D6

    NOTES.forEach((frequency, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = now + index * 0.18;

      osc.type = 'sine';
      osc.frequency.value = frequency;

      // exponential ramps avoid the click an abrupt gain change produces
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.18);
    });
  } catch {
    /* audio is a nicety; never let it break the page */
  }
}
